import React, { useState, useEffect } from 'react';
import get from 'lodash/get';
import { useUserContext } from '@nexus/fff-utilities';

import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import EditAddressForm from '../../components/EditAddressForm';
import useUserShippingAddress from '../../hooks/useUserShippingAddress';
import sussesImg from './images/confirmation-image.jpg';
import EditAddressSuggestion from '../../components/EditAddressSuggestion';
import {
  countryNameCodes, addressSuggestionStatus, addressMessages,
} from '../../constants/address';
import { clearCache, updateUserAddress } from '../../services/user';
import { starTimer } from '../../services/newMember';
import Toast from '../../components/Toast';
import './styles.scss';

const ReactivateThanks = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [suggestionModal, setSuggestionModal] = useState({ isOpen: false });
  const [toast, setToast] = useState({ isOpen: false });
  const [address, setAddress] = useState({});
  const shippingAddress = useUserShippingAddress();
  const { userInfo } = useUserContext();

  useEffect(() => {
    if (shippingAddress) {
      setAddress({
        countryCode: get(countryNameCodes, get(shippingAddress, 'country')),
        primaryLine: get(shippingAddress, 'address1'),
        secondaryLine: get(shippingAddress, 'address2'),
        city: get(shippingAddress, 'city'),
        state: get(shippingAddress, 'region'),
        zip: get(shippingAddress, 'code'),
      });
    }
  }, [shippingAddress]);

  const openSussesToast = () => (
    setToast({
      isOpen: true,
      hasErrors: false,
      title: 'Primary Address Updated',
      message: 'Your primary shipping address changes have been successfully saved.',
    })
  );

  const openErrorToast = () => (
    setToast({
      isOpen: true,
      hasErrors: true,
      title: 'Primary Address Update Failed',
      message: 'Your primary shipping address was not updated. Please try again.',
    })
  );

  const closeToast = () => setToast({ ...toast, isOpen: false });

  const handleSubmit = async (response) => {
    const { modalType, submitted } = response;

    if (modalType === addressSuggestionStatus.VERIFIED) {
      const { success } = await updateUserAddress(submitted);
      if (success) {
        openSussesToast();
      } else {
        openErrorToast();
      }
      clearCache();
    } else if (
      modalType === addressSuggestionStatus.NO_SUGGESTION || modalType === addressSuggestionStatus.SUGGESTION_AVAILABLE
    ) {
      setSuggestionModal({
        isOpen: true,
        subtitle: addressMessages.SUBTITLE_NO_OPTIONS,
      });
    }

    setAddress(submitted);
    setIsModalOpen(false);
  };

  const handleCloseSuggestion = () => {
    setIsModalOpen(true);
    setSuggestionModal({ isOpen: false });
  };

  const handleAcceptSuggestion = async () => {
    const { success } = await updateUserAddress(address);

    if (success) {
      openSussesToast();
    } else {
      openErrorToast();
    }
    setAddress(address);
    clearCache();
    setIsModalOpen(false);
    setSuggestionModal({ isOpen: false });
  };

  const handleContinue = () => {
    starTimer()
      .then((response) => {
        if (response.ok) {
          window.location.replace(process.env.REACT_APP_SITE_URL);
        } else {
          // eslint-disable-next-line no-console
          console.error('Timer is not started', response);
        }
      })
    // eslint-disable-next-line no-console
      .catch((error) => console.error(error));
  };

  return (
    <Layout isNMF useMemorized={false}>
      {isModalOpen && (
        <Modal title="Edit Your Address">
          <EditAddressForm
            onCancel={() => setIsModalOpen(false)}
            onSubmit={handleSubmit}
            address={address}
          />
        </Modal>
      )}
      {suggestionModal.isOpen && (
        <Modal
          title="Oops! Please Verify"
          subtitle={suggestionModal.subtitle}
        >
          <EditAddressSuggestion
            address={address}
            onClose={handleCloseSuggestion}
            onAccept={handleAcceptSuggestion}
          />
        </Modal>
      )}
      <Toast
        isOpen={toast.isOpen}
        onRequestClose={closeToast}
        hasErrors={toast.hasErrors}
        title={toast.title}
        message={toast.message}
      />
      <section className="reactivate-thanks">
        <div className="reactivate-thanks__title mobile">
          <h2>Success! We&apos;re Happy to Have You Back</h2>
        </div>
        <div className="reactivate-thanks__image">
          <img src={sussesImg} alt="susses" />
        </div>
        <div className="reactivate-thanks__address">
          <div className="reactivate-thanks__address__container">
            <div className="reactivate-thanks__title desktop">
              <h2>Success! We&apos;re Happy to Have You Back</h2>
            </div>
            <p className="reactivate-thanks__address__subtitle">Please verify your shipping address</p>
            <div className="reactivate-thanks__address__box">
              <div className="left">
                <p className="name">
                  {get(userInfo, 'user.firstName')}
                  {' '}
                  {get(userInfo, 'user.lastName')}
                </p>
                <p className="address">{get(address, 'primaryLine')}</p>
                {get(address, 'primaryLine') && <p className="address">{get(address, 'secondaryLine')}</p>}
                <p className="city">
                  {get(address, 'city')}
                  {shippingAddress && ', '}
                  {get(address, 'state')}
                  {' '}
                  {get(address, 'zip')}
                </p>
              </div>
              <div className="right">
                {shippingAddress && <button type="button" onClick={() => setIsModalOpen(true)}>Edit Address</button>}
              </div>
            </div>
            <p className="reactivate-thanks__address__advice">
              Address changes will be applied toward your next shipment.
              If you need to update your address for a current shipment please email
              <a href="mailto:vip@fabfitfun.com">vip@fabfitfun.com</a>
              if your shipment has not been processed yet.
            </p>
            <button
              type="button"
              className="reactivate-thanks__link fff-btn-primary"
              onClick={handleContinue}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ReactivateThanks;
